import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

// Activity events that count as "user is here". `mousemove` was deliberately
// dropped — it fires hundreds of times per second and pinned the timer.
// `mousedown` + `click` cover any real interaction.
const IDLE_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown', 'keydown', 'touchstart', 'scroll', 'click',
];

// Coalesce activity bursts (held key, scroll wheel) into at most one
// timer-restart per second. Invisible to users; kills the churn.
const ACTIVITY_THROTTLE_MS = 1000;

// Cross-tab sync. BroadcastChannel is the primary path; the `storage`
// event on STORAGE_KEY is the fallback for browsers where BroadcastChannel
// is unavailable or partitioned (Safari ITP, embedded WebViews).
// Persisting the timestamp also survives reload / laptop sleep.
const STORAGE_KEY = 'kabbi:lastActivity';
const CHANNEL_NAME = 'kabbi-idle';

// Warning dialog shown this many seconds before auto-logout
export const WARNING_SECS = 120;

/**
 * Two-phase idle watcher with cross-tab sync.
 *
 *   1. Idle for (timeoutMin * 60 - WARNING_SECS) seconds → show warning dialog
 *   2. No response for WARNING_SECS more seconds → logout
 *
 * Cross-tab behaviour:
 *   - Activity in any tab resets the timer in every other tab
 *   - Logout in any tab logs out every other tab
 *   - lastActivity is persisted in localStorage so reload / sleep don't reset the clock
 *
 * Returns state + callbacks consumed by <IdleWarningDialog>.
 * Pass timeoutMin = 0 to disable entirely.
 */
export function useIdleLogout(timeoutMin: number) {
  const { logout, isAuthenticated } = useAuthStore();

  const [warningVisible, setWarningVisible] = useState(false);
  const [secondsLeft, setSecondsLeft]       = useState(WARNING_SECS);

  const idleTimer    = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const logoutTimer  = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const countdown    = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningRef   = useRef(false); // mirror of warningVisible for use inside event listeners
  const lastResetRef = useRef(0);     // throttle anchor for activity events
  const channelRef   = useRef<BroadcastChannel | null>(null);

  /** Cancel every outstanding timer (idle / warning-countdown / final logout). */
  const clearAll = useCallback(() => {
    if (idleTimer.current)   clearTimeout(idleTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdown.current)   clearInterval(countdown.current);
  }, []);

  /**
   * Final logout step. Clears timers, tells peer tabs to log out too (unless
   * we got here BECAUSE a peer told us — `broadcast: false`), then redirects.
   */
  const triggerLogout = useCallback((opts: { broadcast?: boolean } = {}) => {
    const broadcast = opts.broadcast ?? true;
    clearAll();
    if (broadcast) {
      try { channelRef.current?.postMessage({ type: 'logout' }); } catch { /* noop */ }
    }
    logout();
    window.location.replace('/login?reason=idle');
  }, [clearAll, logout]);

  /** Show the warning dialog and start the final WARNING_SECS countdown. */
  const showWarning = useCallback(() => {
    setWarningVisible(true);
    warningRef.current = true;

    let secs = WARNING_SECS;
    setSecondsLeft(secs);

    countdown.current = setInterval(() => {
      secs -= 1;
      setSecondsLeft(secs);
    }, 1000);

    logoutTimer.current = setTimeout(() => triggerLogout(), WARNING_SECS * 1000);
  }, [triggerLogout]);

  /**
   * Compute how many ms remain in the silent-idle phase, taking the persisted
   * lastActivity into account. <=0 means we're already past the threshold (e.g.
   * user reloaded or the laptop slept) and the warning should fire immediately.
   */
  const computeRemainingMs = useCallback(() => {
    const totalIdleMs = Math.max((timeoutMin * 60 - WARNING_SECS) * 1000, 0);
    let lastActivity = 0;
    try { lastActivity = Number(localStorage.getItem(STORAGE_KEY)) || 0; } catch { /* noop */ }
    if (!lastActivity) return totalIdleMs;
    return totalIdleMs - (Date.now() - lastActivity);
  }, [timeoutMin]);

  /** (Re)start the silent-idle timer based on the current persisted lastActivity. */
  const startIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    const remaining = computeRemainingMs();
    if (remaining <= 0) { showWarning(); return; }
    idleTimer.current = setTimeout(showWarning, remaining);
  }, [computeRemainingMs, showWarning]);

  /**
   * Record a local activity tick: throttle, persist timestamp, broadcast to
   * peers, restart the idle timer. Skipped while the warning is up — the user
   * must explicitly click "Continue" in that case.
   */
  const recordActivity = useCallback(() => {
    if (warningRef.current) return;
    const now = Date.now();
    if (now - lastResetRef.current < ACTIVITY_THROTTLE_MS) return;
    lastResetRef.current = now;
    try { localStorage.setItem(STORAGE_KEY, String(now)); } catch { /* noop */ }
    try { channelRef.current?.postMessage({ type: 'activity', t: now }); } catch { /* noop */ }
    startIdleTimer();
  }, [startIdleTimer]);

  /** Dismiss any locally-shown warning silently (called from peer-activity handlers). */
  const dismissWarning = useCallback(() => {
    if (!warningRef.current) return;
    clearAll();
    setWarningVisible(false);
    warningRef.current = false;
    setSecondsLeft(WARNING_SECS);
  }, [clearAll]);

  /** "Continue" button in the warning dialog: dismiss + treat as fresh activity. */
  const continueSession = useCallback(() => {
    dismissWarning();
    lastResetRef.current = 0; // bypass throttle so this tick definitely registers
    recordActivity();
  }, [dismissWarning, recordActivity]);

  useEffect(() => {
    if (!isAuthenticated || !timeoutMin || timeoutMin <= 0) return;

    // Treat the auth transition as fresh activity — without this, a stale
    // `kabbi:lastActivity` from a previous session (or a long-closed tab)
    // makes computeRemainingMs() return a negative value and the warning
    // dialog fires immediately on login.
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    lastResetRef.current = Date.now();

    // Open the cross-tab channel (graceful no-op if unsupported).
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (e: MessageEvent) => {
        const msg = e.data as { type?: 'activity' | 'logout' };
        if (msg?.type === 'logout') {
          // Peer logged out — follow them down without re-broadcasting.
          triggerLogout({ broadcast: false });
        } else if (msg?.type === 'activity') {
          // Peer activity is treated as global "user is here": dismiss any
          // local warning silently and reset the timer. Do NOT re-broadcast.
          dismissWarning();
          lastResetRef.current = Date.now();
          startIdleTimer();
        }
      };
    } catch { channelRef.current = null; }

    // Storage-event fallback for browsers without BroadcastChannel. Same
    // semantics as the channel handler — dismiss local warning, no echo.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      dismissWarning();
      lastResetRef.current = Number(e.newValue) || Date.now();
      startIdleTimer();
    };

    // visibilitychange: when the tab regains focus (laptop wake / tab switch),
    // recompute against persisted lastActivity. computeRemainingMs handles the
    // "already expired while we were hidden" path.
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (warningRef.current) return;
      startIdleTimer();
    };

    const onActivity = () => recordActivity();

    IDLE_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);

    startIdleTimer();

    return () => {
      clearAll();
      IDLE_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      try { channelRef.current?.close(); } catch { /* noop */ }
      channelRef.current = null;
    };
  }, [isAuthenticated, timeoutMin, startIdleTimer, recordActivity, triggerLogout, clearAll, dismissWarning]);

  return { warningVisible, secondsLeft, continueSession, doLogout: () => triggerLogout() };
}
